/**
 * New set of analytics category definitions
 * This is in accordance with what Segment has written over here;
 * - https://segment.com/academy/collecting-data/naming-conventions-for-clean-data/
 * - https://segment.com/docs/connections/spec/b2b-saas/)
 *
 * Broad example;
 * [Location] [Object] [Action]
 * - user_type: 'admin' | 'reference' | 'candidate' | 'hirer'
 * - ...
 *
 * Specific examples;
 * Admin Dashboard Reference Requests Opened
 * - user_type: 'admin'
 * - access_code: '4mkBU3Bk3qd0c0U6_9Jwn'
 * - access_url: 'http://localhost:3000/referencermagiclink/phil.austin@veranahealth.com/4mkBU3Bk3qd0c0U6_9Jwn'
 * - ...
 *
 * Assessments Hiring Survey Clicked Get Started
 * - user_type: 'reference'
 * - reference_request_id: '43756'
 * - ...
 */

import { NewSurveyObjectActionsType } from 'components/SurveyExperience/analyticsTypes';
import { SurveysHomeObjectActionsType } from 'components/SurveysHomePage/analyticsTypes';
import { NavBarObjectActionsType } from 'components/UserNav/analyticsTypes';

/**
 * Adding a new set of Analytics events from scratch
 * 1. First we add a Location to our AnalyticsLocation enum
 * 2. We define a new enum named [Location]Objects
 *  This is what we will be adding the different portions of our app to, for example
 * the ReferenceRequestAccessCode is a piece of clickable text in the AdminDashboard
 * 3. We add an Object by name in Proper Case
 *  This looks like ReferenceRequestAccessCode = 'Reference Request Access Code'
 * 4. We then add an enum specific to this Object as [Location][Object]Actions
 *  This will contain all of the possible interactions a user can make that we
 * would want to track
 * 5. We then add our Combined Type as [Location][Object][Action]Type
 *  This weaves together the names and provides a layer of type safety for the event
 * 6. Then, for each of the defined Combined Types, we OR them together into
 * a [Location]ObjectsActionsType
 *  See the AdminDashboardObjectActionsType below for an example
 * 7. The Combined Type is OR'd into our AnalyticsEvent type, and is now available
 *
 * Adding to an existing set of Analytics events
 * 1. First, add to the Object, we will use AdminDashboardObjects as an example
 *  We could add a `LogoutButton = 'Logout Button'` to this enum
 * 2. Then we define our Actions, in this case it will be an enum called AdminDashboardLogoutButtonActions
 * 3. We add `Clicked = 'Clicked'` to our AdminDashboardLogoutButtonActions enum
 *  Because a user can click a logout button, and we want to track it
 * 4. Then we add our Combined Type for type safety as "type AdminDashboardLogoutButtonActionsType = `${AnalyticsLocations.AdminDashboard} ${AdminDashboardObjects.LogoutButton} ${AdminDashboardLogoutButtonActions}`"
 * 5. OR the Combined Type to our existing AdminDashboardObjectActionsType, and we're done
 */

/**
 * Locations - the pages and major features in which the Object is located
 */
export enum AnalyticsLocations {
  AdminDashboard = 'Admin Dashboard',
  Survey = 'Survey',
  CandidateHub = 'Candidate Hub',
  PDFViewer = 'PDF Viewer',
  QualityOfHire = 'Quality Of Hire',
  AutomationSettings = 'Automation Settings',
  EmployeeReviewers = 'Employee Reviewers',
  CandidateExperience = 'Candidate Experience',
  Intake = 'Intake',
  PositionHub = 'PositionHub',
  ActivitiesQueue = 'Activities Queue',
  HrisSettings = 'HRIS Settings',
  FeatureFlags = 'Feature Flags',
  SurveysHome = 'Surveys Home',
  NavBar = 'Nav Bar',
  SkillsAndCulture = 'Skills And Culture',
  TalentModels = 'Talent Models',
  Explainability = 'Explainability',
  YourTeams = 'Your Teams',
  TalentDiscovery = 'Talent Discovery',
}

/**
 * ActivitiesQueue
 */

enum ActivitiesQueueObjects {
  Tab = 'Tab',
  ActivityCard = 'Activity Card',
}

enum ActivitiesQueueTabActions {
  Clicked = 'Clicked',
}

type ActivitiesQueueTabType =
  `${AnalyticsLocations.ActivitiesQueue} ${ActivitiesQueueObjects.Tab} ${ActivitiesQueueTabActions}`;

enum ActivitiesQueueActivityActions {
  Clicked = 'Clicked',
}
type ActivitiesQueueActivityType =
  `${AnalyticsLocations.ActivitiesQueue} ${ActivitiesQueueObjects.ActivityCard} ${ActivitiesQueueActivityActions}`;

type ActivitiesQueueObjectActionsType = ActivitiesQueueTabType | ActivitiesQueueActivityType;
/**
 * Benchmark/Intake
 */
enum IntakeObjects {
  Comment = 'Comment',
  Note = 'Note',
  Collaborator = 'Collaborator',
  DropdownDelete = 'Dropdown Delete',
  DropdownDuplicate = 'Dropdown Duplicate',
  Survey = 'Survey',
  SurveyRespondent = 'SurveyRespondent',
  Response = 'Response',
}

enum IntakeDropdownDeleteActions {
  Clicked = 'Clicked',
}

type IntakeDropdownDeleteType =
  `${AnalyticsLocations.Intake} ${IntakeObjects.DropdownDelete} ${IntakeDropdownDeleteActions}`;

enum IntakeDropdownDuplicateActions {
  Clicked = 'Clicked',
}

type IntakeDropdownDuplicateType =
  `${AnalyticsLocations.Intake} ${IntakeObjects.DropdownDuplicate} ${IntakeDropdownDuplicateActions}`;

enum IntakeCommentActions {
  Added = 'Added',
  Edited = 'Edited',
  Deleted = 'Deleted',
}

type IntakeCommentActionsType =
  `${AnalyticsLocations.Intake} ${IntakeObjects.Comment} ${IntakeCommentActions}`;

enum IntakeNoteActions {
  Added = 'Added',
  Edited = 'Edited',
  Deleted = 'Deleted',
}

type IntakeNoteActionsType =
  `${AnalyticsLocations.Intake} ${IntakeObjects.Note} ${IntakeNoteActions}`;

enum IntakeCollaboratorActions {
  Invited = 'Invited',
  InvitedByEmail = 'Invited By Email',
  Edited = 'Edited',
}

type IntakeCollaboratorActionsType =
  `${AnalyticsLocations.Intake} ${IntakeObjects.Collaborator} ${IntakeCollaboratorActions}`;

enum IntakeSurveyActions {
  Viewed = 'Viewed',
  AddedResponse = 'Added Response',
  AnsweredThroughSurveyExperience = 'Answered Through Survey Experience',
}

type IntakeSurveyActionsType =
  `${AnalyticsLocations.Intake} ${IntakeObjects.Survey} ${IntakeSurveyActions}`;

enum IntakeSurveyRespondentActions {
  Invited = 'Invited',
  InvitedByEmail = 'Invited By Email',
  EditedReminder = 'Edited Reminder',
  EditedNotification = 'Edited Notification',
  Deleted = 'Deleted',
  RequestedResubmission = 'Requested Resubmission',
}

type IntakeSurveyRespondentActionsType =
  `${AnalyticsLocations.Intake} ${IntakeObjects.SurveyRespondent} ${IntakeSurveyRespondentActions}`;

export type IntakeObjectActionsType =
  | IntakeDropdownDuplicateType
  | IntakeDropdownDeleteType
  | IntakeCommentActionsType
  | IntakeNoteActionsType
  | IntakeCollaboratorActionsType
  | IntakeSurveyActionsType
  | IntakeSurveyRespondentActionsType;

export const HIRER_INTAKE_ANALYTICS_EVENTS = {
  NOTE_ADDED: `${AnalyticsLocations.Intake} ${IntakeObjects.Note} ${IntakeNoteActions.Added}`,
  NOTE_EDITED: `${AnalyticsLocations.Intake} ${IntakeObjects.Note} ${IntakeNoteActions.Edited}`,
  NOTE_DELETED: `${AnalyticsLocations.Intake} ${IntakeObjects.Note} ${IntakeNoteActions.Deleted}`,

  COMMENT_ADDED: `${AnalyticsLocations.Intake} ${IntakeObjects.Comment} ${IntakeCommentActions.Added}`,
  COMMENT_EDITED: `${AnalyticsLocations.Intake} ${IntakeObjects.Comment} ${IntakeCommentActions.Edited}`,
  COMMENT_DELETED: `${AnalyticsLocations.Intake} ${IntakeObjects.Comment} ${IntakeCommentActions.Deleted}`,

  SURVEY_VIEWED: `${AnalyticsLocations.Intake} ${IntakeObjects.Survey} ${IntakeSurveyActions.Viewed}`,
  SURVEY_ADDED: `${AnalyticsLocations.Intake} ${IntakeObjects.Survey} ${IntakeSurveyActions.AddedResponse}`,
  SURVEY_ANSWERED_THROUGH_SURVEY_EXPERIENCE: `${AnalyticsLocations.Intake} ${IntakeObjects.Survey} ${IntakeSurveyActions.AnsweredThroughSurveyExperience}`,

  DELETED: `${AnalyticsLocations.Intake} ${IntakeObjects.DropdownDelete} ${IntakeDropdownDeleteActions.Clicked}`,
  DUPLICATED: `${AnalyticsLocations.Intake} ${IntakeObjects.DropdownDuplicate} ${IntakeDropdownDuplicateActions.Clicked}`,

  COLLABORATOR_EDITED: `${AnalyticsLocations.Intake} ${IntakeObjects.Collaborator} ${IntakeCollaboratorActions.Edited}`,
  COLLABORATOR_INVITED: `${AnalyticsLocations.Intake} ${IntakeObjects.Collaborator} ${IntakeCollaboratorActions.Invited}`,
  COLLABORATOR_INVITED_BY_EMAIL: `${AnalyticsLocations.Intake} ${IntakeObjects.Collaborator} ${IntakeCollaboratorActions.InvitedByEmail}`,

  SURVEY_RESPONDENT_EDITED_REMINDER: `${AnalyticsLocations.Intake} ${IntakeObjects.SurveyRespondent} ${IntakeSurveyRespondentActions.EditedReminder}`,
  SURVEY_RESPONDENT_EDITED_NOTIFICATION: `${AnalyticsLocations.Intake} ${IntakeObjects.SurveyRespondent} ${IntakeSurveyRespondentActions.EditedNotification}`,
  SURVEY_RESPONDENT_INVITED_NEW_HIRER_BY_EMAIL: `${AnalyticsLocations.Intake} ${IntakeObjects.SurveyRespondent} ${IntakeSurveyRespondentActions.InvitedByEmail}`,
  SURVEY_RESPONDENT_INVITED: `${AnalyticsLocations.Intake} ${IntakeObjects.SurveyRespondent} ${IntakeSurveyRespondentActions.Invited}`,
  SURVEY_RESPONDENT_DELETED: `${AnalyticsLocations.Intake} ${IntakeObjects.SurveyRespondent} ${IntakeSurveyRespondentActions.Deleted}`,
  SURVEY_RESPONDENT_REQUESTED_RESUBMISSION: `${AnalyticsLocations.Intake} ${IntakeObjects.SurveyRespondent} ${IntakeSurveyRespondentActions.RequestedResubmission}`,
} as Record<string, IntakeObjectActionsType>;

/**
 * Admin Dashboard
 *
 * Below follows a verbosely commented example of how to set up the type-safety for events,
 * beneath it are more condensed versions that enumerate everything else we're tracking
 */
/**
 * Objects - the relevant part of the system that incurred an Action
 */
enum AdminDashboardObjects {
  ReferenceRequestAccessCode = 'Reference Request Access Code',
}
/**
 * Action - what actually happened
 */
enum AdminDashboardReferenceRequestAccessCodeActions {
  Opened = 'Opened',
}

/**
 * Combined '[Location] [Object] [Action]' for type-safety
 */
type AdminDashboardReferenceRequestAccessCodeActionsType =
  `${AnalyticsLocations.AdminDashboard} ${AdminDashboardObjects.ReferenceRequestAccessCode} ${AdminDashboardReferenceRequestAccessCodeActions}`;

/**
 * Final type, to be OR'd with any other [Location][Object]ActionsType we have as follows;
 * type AdminDashboardObjectActionsType = AdminDashboardReferenceRequestAccessCodeActionsType
 * | AdminDashboardReferenceRequestExitButtonActionsType
 * | AdminDashboardReferenceRequestSupportPopupButtonActionsType
 */
type AdminDashboardObjectActionsType = AdminDashboardReferenceRequestAccessCodeActionsType;

/**
 * Reference Survey
 */
enum ReferenceSurveyObjects {
  GetStartedButton = 'Get Started Button',
  SettingsWheel = 'Settings Wheel',
  ChatWithUs = 'Chat With Us',
  DeclineToProvideFeedback = 'Decline To Provide Feedback',
  DeclinationReason = 'Declination Reason',
  DeclinationCancelButton = 'Declination Cancel Button',
  ViewAs1Page = 'View As 1 Page',
  ContinueButton = 'Continue Button',
  SkipButton = 'Skip Button',
  BackButton = 'Back Button',
  Answer = 'Answer',
  LearnAboutNewJobOpportunitiesCheckbox = 'Learn About New Job Opportunities Checkbox',
  UseSearchlightForYourOwnHiringCheckbox = 'Use Searchlight For Your Own Hiring Checkbox',
  SubmitButton = 'Submit Button',
}

enum ReferenceSurveyGetStartedButtonActions {
  Clicked = 'Clicked',
}
type ReferenceSurveyGetStartedButtonActionsType =
  `${AnalyticsLocations.Survey} ${ReferenceSurveyObjects.GetStartedButton} ${ReferenceSurveyGetStartedButtonActions}`;

enum ReferenceSurveySettingsWheelActions {
  Toggled = 'Toggled',
}
type ReferenceSurveySettingsWheelActionsType =
  `${AnalyticsLocations.Survey} ${ReferenceSurveyObjects.SettingsWheel} ${ReferenceSurveySettingsWheelActions}`;

enum ReferenceSurveyChatWithUsActions {
  Clicked = 'Clicked',
}
type ReferenceSurveyChatWithUsActionsType =
  `${AnalyticsLocations.Survey} ${ReferenceSurveyObjects.ChatWithUs} ${ReferenceSurveyChatWithUsActions}`;

enum ReferenceSurveyDeclineToProvideFeedbackActions {
  Clicked = 'Clicked',
}
type ReferenceSurveyDeclineToProvideFeedbackActionsType =
  `${AnalyticsLocations.Survey} ${ReferenceSurveyObjects.DeclineToProvideFeedback} ${ReferenceSurveyDeclineToProvideFeedbackActions}`;

enum ReferenceSurveyViewAs1PageActions {
  Clicked = 'Clicked',
}
type ReferenceSurveyViewAs1PageActionsType =
  `${AnalyticsLocations.Survey} ${ReferenceSurveyObjects.ViewAs1Page} ${ReferenceSurveyViewAs1PageActions}`;

enum ReferenceSurveyContinueButtonActions {
  Clicked = 'Clicked',
}
type ReferenceSurveyContinueButtonActionsType =
  `${AnalyticsLocations.Survey} ${ReferenceSurveyObjects.ContinueButton} ${ReferenceSurveyContinueButtonActions}`;

enum ReferenceSurveySkipButtonActions {
  Clicked = 'Clicked',
}
type ReferenceSurveySkipButtonActionsType =
  `${AnalyticsLocations.Survey} ${ReferenceSurveyObjects.SkipButton} ${ReferenceSurveySkipButtonActions}`;

enum ReferenceSurveyBackButtonActions {
  Clicked = 'Clicked',
}
type ReferenceSurveyBackButtonActionsType =
  `${AnalyticsLocations.Survey} ${ReferenceSurveyObjects.BackButton} ${ReferenceSurveyBackButtonActions}`;

enum ReferenceSurveyAnswerActions {
  Changed = 'Changed',
  Blurred = 'Blurred',
}
type ReferenceSurveyAnswerActionsType =
  `${AnalyticsLocations.Survey} ${ReferenceSurveyObjects.Answer} ${ReferenceSurveyAnswerActions}`;

enum ReferenceSurveyDeclinationReasonActions {
  Selected = 'Selected',
  Submitted = 'Submitted',
}
type ReferenceSurveyDeclinationReasonActionsType =
  `${AnalyticsLocations.Survey} ${ReferenceSurveyObjects.DeclinationReason} ${ReferenceSurveyDeclinationReasonActions}`;

enum ReferenceSurveyDeclinationCancelButtonActions {
  Clicked = 'Clicked',
}
type ReferenceSurveyDeclinationCancelButtonActionsType =
  `${AnalyticsLocations.Survey} ${ReferenceSurveyObjects.DeclinationCancelButton} ${ReferenceSurveyDeclinationCancelButtonActions}`;

enum ReferenceSurveyLearnAboutNewJobOpportunitiesCheckboxActions {
  Checked = 'Checked',
}
type ReferenceSurveyLearnAboutNewJobOpportunitiesCheckboxActionsType =
  `${AnalyticsLocations.Survey} ${ReferenceSurveyObjects.LearnAboutNewJobOpportunitiesCheckbox} ${ReferenceSurveyLearnAboutNewJobOpportunitiesCheckboxActions}`;

enum ReferenceSurveyUseSearchlightForYourOwnHiringCheckboxActions {
  Checked = 'Checked',
}
type ReferenceSurveyUseSearchlightForYourOwnHiringCheckboxActionsType =
  `${AnalyticsLocations.Survey} ${ReferenceSurveyObjects.UseSearchlightForYourOwnHiringCheckbox} ${ReferenceSurveyUseSearchlightForYourOwnHiringCheckboxActions}`;

enum ReferenceSurveySubmitButtonActions {
  Clicked = 'Clicked',
}
type ReferenceSurveySubmitButtonActionsType =
  `${AnalyticsLocations.Survey} ${ReferenceSurveyObjects.SubmitButton} ${ReferenceSurveySubmitButtonActions}`;

type ReferenceSurveyObjectActionsType =
  | ReferenceSurveyGetStartedButtonActionsType
  | ReferenceSurveySettingsWheelActionsType
  | ReferenceSurveyChatWithUsActionsType
  | ReferenceSurveyDeclineToProvideFeedbackActionsType
  | ReferenceSurveyViewAs1PageActionsType
  | ReferenceSurveyContinueButtonActionsType
  | ReferenceSurveySkipButtonActionsType
  | ReferenceSurveyBackButtonActionsType
  | ReferenceSurveyAnswerActionsType
  | ReferenceSurveyDeclinationReasonActionsType
  | ReferenceSurveyDeclinationCancelButtonActionsType
  | ReferenceSurveyLearnAboutNewJobOpportunitiesCheckboxActionsType
  | ReferenceSurveyUseSearchlightForYourOwnHiringCheckboxActionsType
  | ReferenceSurveySubmitButtonActionsType;

/**
 * Candidate Hub
 */
enum CandidateHubObjects {
  DownloadReportButton = 'Download Report Button',
  NavigationTab = 'Navigation Tab',
  ShareButton = 'Share Button',
  CentralHubLink = 'Central Hub Link',
}

enum CandidateHubCentralHubLinkActions {
  Clicked = 'Clicked',
}

type CandidateHubCentralHubLinkActionsType =
  `${AnalyticsLocations.CandidateHub} ${CandidateHubObjects.CentralHubLink} ${CandidateHubCentralHubLinkActions}`;

enum CandidateHubDownloadReportButtonActions {
  Clicked = 'Clicked',
}
type CandidateHubDownloadReportButtonActionsType =
  `${AnalyticsLocations.CandidateHub} ${CandidateHubObjects.DownloadReportButton} ${CandidateHubDownloadReportButtonActions}`;

enum CandidateHubNavigationTabActions {
  Clicked = 'Clicked',
}
type CandidateHubNavigationTabActionsType =
  `${AnalyticsLocations.CandidateHub} ${CandidateHubObjects.NavigationTab} ${CandidateHubNavigationTabActions}`;

enum CandidateHubShareButtonActions {
  Clicked = 'Clicked',
}
type CandidateHubShareButtonActionsType =
  `${AnalyticsLocations.CandidateHub} ${CandidateHubObjects.ShareButton} ${CandidateHubShareButtonActions}`;

type CandidateHubObjectActionsType =
  | CandidateHubDownloadReportButtonActionsType
  | CandidateHubNavigationTabActionsType
  | CandidateHubShareButtonActionsType
  | CandidateHubCentralHubLinkActionsType;

/**
 * PDF Viewer
 */
enum PDFViewerObjects {
  DownloadButton = 'Download Button',
}

enum PDFViewerDownloadButtonActions {
  Clicked = 'Clicked',
}
type PDFViewerDownloadButtonActionsType =
  `${AnalyticsLocations.PDFViewer} ${PDFViewerObjects.DownloadButton} ${PDFViewerDownloadButtonActions}`;

type PDFViewerObjectActionsType = PDFViewerDownloadButtonActionsType;

/**
 * Quality of Hire
 */
enum QualityOfHireObjects {
  NavigationTab = 'Navigation Tab',
  ScoreOverTimeGraph = 'Score Over Time Graph',
  Formula = 'Formula',
  FactorGraph = 'Factor Graph',
  CohortScoreByReviewCycleGraph = 'Cohort Score By Review Graph',
  CohortSummaryGraph = 'Cohort Summary Graph',
  CohortResponseGraph = 'Cohort Response Graph',
  ReviewResponseGraph = 'Review Response Graph',
  SelfAssessmentResponseGraph = 'Self Assessment Response Graph',
  DrillDownTable = 'DrillDownTable',
  DrilldownTableRow = 'Drilldown Table Row',
  ReviewResponseDrillDownTableRow = 'Review Response Drilldown Table Row',
  CohortResponseDrillDownTableRow = 'Cohort Response Drilldown Table Row',
  CohortSummaryDrillDownTableRow = 'Cohort Summary Drilldown Table Row',
  SelfAssessmentResponseDrillDownTableRow = 'Cohort Response Drilldown Table Row',
}

enum QualityOfHireNavigationTabActions {
  Clicked = 'Clicked',
}
type QualityOfHireNavigationTabActionsType =
  `${AnalyticsLocations.QualityOfHire} ${QualityOfHireObjects.NavigationTab} ${QualityOfHireNavigationTabActions}`;

enum QualityOfHireScoreOverTimeGraphActions {
  Clicked = 'Clicked',
}
type QualityOfHireScoreOverTimeGraphActionsType =
  `${AnalyticsLocations.QualityOfHire} ${QualityOfHireObjects.ScoreOverTimeGraph} ${QualityOfHireScoreOverTimeGraphActions}`;

enum QualityOfHireFormulaActions {
  Clicked = 'Clicked',
}
type QualityOfHireFormulaActionsType =
  `${AnalyticsLocations.QualityOfHire} ${QualityOfHireObjects.Formula} ${QualityOfHireFormulaActions}`;

enum QualityOfHireFactorGraphActions {
  Clicked = 'Clicked',
}
type QualityOfHireFactorGraphActionsType =
  `${AnalyticsLocations.QualityOfHire} ${QualityOfHireObjects.FactorGraph} ${QualityOfHireFactorGraphActions}`;

enum QualityOfHireCohortScoreByReviewCycleGraphActions {
  Clicked = 'Clicked',
}
type QualityOfHireCohortScoreByReviewCycleGraphActionsType =
  `${AnalyticsLocations.QualityOfHire} ${QualityOfHireObjects.CohortScoreByReviewCycleGraph} ${QualityOfHireCohortScoreByReviewCycleGraphActions}`;

enum QualityOfHireCohortSummaryGraphActions {
  Clicked = 'Clicked',
}
type QualityOfHireCohortSummaryGraphActionsType =
  `${AnalyticsLocations.QualityOfHire} ${QualityOfHireObjects.CohortSummaryGraph} ${QualityOfHireCohortSummaryGraphActions}`;

enum QualityOfHireCohortResponseGraphActions {
  Clicked = 'Clicked',
}
type QualityOfHireCohortResponseGraphActionsType =
  `${AnalyticsLocations.QualityOfHire} ${QualityOfHireObjects.CohortResponseGraph} ${QualityOfHireCohortResponseGraphActions}`;

enum QualityOfHireReviewResponseGraphActions {
  Clicked = 'Clicked',
}
type QualityOfHireReviewResponseGraphActionsType =
  `${AnalyticsLocations.QualityOfHire} ${QualityOfHireObjects.ReviewResponseGraph} ${QualityOfHireReviewResponseGraphActions}`;

enum QualityOfHireSelfAssessmentResponseGraphActions {
  Clicked = 'Clicked',
}
type QualityOfHireSelfAssessmentResponseGraphActionsType =
  `${AnalyticsLocations.QualityOfHire} ${QualityOfHireObjects.SelfAssessmentResponseGraph} ${QualityOfHireSelfAssessmentResponseGraphActions}`;

enum QualityOfHireDrilldownTableRowActions {
  Clicked = 'Clicked',
}

type QualityOfHireDrilldownTableRowActionsType =
  `${AnalyticsLocations.QualityOfHire} ${QualityOfHireObjects.DrilldownTableRow} ${QualityOfHireDrilldownTableRowActions}`;

type QualityOfHireObjectActionsType =
  | QualityOfHireNavigationTabActionsType
  | QualityOfHireScoreOverTimeGraphActionsType
  | QualityOfHireFormulaActionsType
  | QualityOfHireFactorGraphActionsType
  | QualityOfHireCohortScoreByReviewCycleGraphActionsType
  | QualityOfHireCohortSummaryGraphActionsType
  | QualityOfHireCohortResponseGraphActionsType
  | QualityOfHireReviewResponseGraphActionsType
  | QualityOfHireSelfAssessmentResponseGraphActionsType
  | QualityOfHireDrilldownTableRowActionsType;

/**
 * Employee Reviewers
 */
enum EmployeeReviewersObjects {
  UpdateReviewers = 'Update Reviewers',
  TeamFilter = 'Team Filter',
  Pagination = 'Pagination',
  AddReviewerButton = 'Add Reviewer Button',
  DeleteReviewerButton = 'Delete Reviewer Button',
  EmployeeSearchBar = 'Employee Search Bar',
  UploadCSVButton = 'Upload CSV Button',
}

enum EmployeeReviewersUpdateReviewersActions {
  Clicked = 'Clicked',
}
type EmployeeReviewersNavigationTabActionsType =
  `${AnalyticsLocations.EmployeeReviewers} ${EmployeeReviewersObjects.UpdateReviewers} ${EmployeeReviewersUpdateReviewersActions}`;

enum EmployeeReviewersTeamFilterActions {
  Applied = 'Applied',
}
type EmployeeReviewersTeamFilterActionsType =
  `${AnalyticsLocations.EmployeeReviewers} ${EmployeeReviewersObjects.TeamFilter} ${EmployeeReviewersTeamFilterActions}`;

enum EmployeeReviewersPaginationActions {
  Clicked = 'Clicked',
}
type EmployeeReviewersPaginationActionsType =
  `${AnalyticsLocations.EmployeeReviewers} ${EmployeeReviewersObjects.Pagination} ${EmployeeReviewersPaginationActions}`;

enum EmployeeReviewersAddReviewerButtonActions {
  Clicked = 'Clicked',
}
type EmployeeReviewersAddReviewerButtonActionsType =
  `${AnalyticsLocations.EmployeeReviewers} ${EmployeeReviewersObjects.AddReviewerButton} ${EmployeeReviewersAddReviewerButtonActions}`;

enum EmployeeReviewersDeleteReviewerButtonActions {
  Clicked = 'Clicked',
}
type EmployeeReviewersDeleteReviewerButtonActionsType =
  `${AnalyticsLocations.EmployeeReviewers} ${EmployeeReviewersObjects.DeleteReviewerButton} ${EmployeeReviewersDeleteReviewerButtonActions}`;

enum EmployeeReviewersEmployeeSearchBarActions {
  Queried = 'Queried',
}
type EmployeeReviewersEmployeeSearchBarActionsType =
  `${AnalyticsLocations.EmployeeReviewers} ${EmployeeReviewersObjects.EmployeeSearchBar} ${EmployeeReviewersEmployeeSearchBarActions}`;

enum EmployeeReviewersUploadCSVButtonActions {
  Clicked = 'Clicked',
}
type EmployeeReviewersUploadCSVButtonActionsType =
  `${AnalyticsLocations.EmployeeReviewers} ${EmployeeReviewersObjects.UploadCSVButton} ${EmployeeReviewersUploadCSVButtonActions}`;

type EmployeeReviewersObjectActionsType =
  | EmployeeReviewersNavigationTabActionsType
  | EmployeeReviewersTeamFilterActionsType
  | EmployeeReviewersPaginationActionsType
  | EmployeeReviewersAddReviewerButtonActionsType
  | EmployeeReviewersDeleteReviewerButtonActionsType
  | EmployeeReviewersEmployeeSearchBarActionsType
  | EmployeeReviewersUploadCSVButtonActionsType;

/*
 * Candidate Experience
 */
enum CandidateExperienceObjects {
  PublicProfileSettingsProfilePic = 'Public Profile Settings Profile Pic',
  SideBarProfilePic = 'Side Bar Profile Pic',
  SelfAssessment = 'Self Assessment',
  PublicProfileSettingsPublicReferences = 'Public Profile Settings Public References',
  NeedHelpButton = 'Need Help Button',
}

enum CandidateExperienceNeedHelpButtonActions {
  Clicked = 'Clicked',
}

type CandidateExperienceNeedHelpButtonActionsType =
  `${AnalyticsLocations.CandidateExperience} ${CandidateExperienceObjects.NeedHelpButton} ${CandidateExperienceNeedHelpButtonActions}`;

enum CandidateExperiencePublicProfileSettingsProfilePicActions {
  Uploaded = 'Uploaded',
}

type CandidateExperiencePublicProfileSettingsActionsType =
  `${AnalyticsLocations.CandidateExperience} ${CandidateExperienceObjects.PublicProfileSettingsProfilePic} ${CandidateExperiencePublicProfileSettingsProfilePicActions}`;

enum CandidateExperienceSideBarProfilePicActions {
  Uploaded = 'Uploaded',
}

type CandidateExperienceSideBarActionsType =
  `${AnalyticsLocations.CandidateExperience} ${CandidateExperienceObjects.SideBarProfilePic} ${CandidateExperienceSideBarProfilePicActions}`;

enum CandidateExperienceSelfAssessmentActions {
  Submitted = 'Submitted',
}

type CandidateExperienceSelfAssessmentActionsType =
  `${AnalyticsLocations.CandidateExperience} ${CandidateExperienceObjects.SelfAssessment} ${CandidateExperienceSelfAssessmentActions}`;

enum CandidateExperiencePublicProfileSettingsPublicReferencesActions {
  Updated = 'Updated',
}

type CandidateExperiencePublicProfileSettingsPublicReferencesActionsType =
  `${AnalyticsLocations.CandidateExperience} ${CandidateExperienceObjects.PublicProfileSettingsPublicReferences} ${CandidateExperiencePublicProfileSettingsPublicReferencesActions}`;

type CandidateExperienceObjectActionsType =
  | CandidateExperiencePublicProfileSettingsActionsType
  | CandidateExperienceSideBarActionsType
  | CandidateExperienceSelfAssessmentActionsType
  | CandidateExperiencePublicProfileSettingsPublicReferencesActionsType
  | CandidateExperienceNeedHelpButtonActionsType;

/**
 * HRIS Settings
 */

enum HRISSettingsObjects {
  UpdateWithCSVButton = 'Update With CSV Button',
  UploadCSVButton = 'Upload CSV Button',
  MergeEmployeesTableRow = 'Merge Employees Table Row',
  MergeEmployeesTableRowDeleteButton = 'Merge Employees Table Row Delete Button',
}

enum HRISSettingsMergeEmployeesTableRowActions {
  Clicked = 'Clicked',
}

type HRISSettingsMergeEmployeesTableRowActionsType =
  `${AnalyticsLocations.HrisSettings} ${HRISSettingsObjects.MergeEmployeesTableRow} ${HRISSettingsMergeEmployeesTableRowActions}`;

enum HRISSettingsMergeEmployeesTableRowDeleteButtonActions {
  Clicked = 'Clicked',
}

type HRISSettingsMergeEmployeesTableRowDeleteButtonActionsType =
  `${AnalyticsLocations.HrisSettings} ${HRISSettingsObjects.MergeEmployeesTableRowDeleteButton} ${HRISSettingsMergeEmployeesTableRowDeleteButtonActions}`;

enum HRISSettingsUpdateWithCSVButtonActions {
  Clicked = 'Clicked',
}

type HRISSettingsUpdateWithCSVButtonActionsType =
  `${AnalyticsLocations.HrisSettings} ${HRISSettingsObjects.UpdateWithCSVButton} ${HRISSettingsUpdateWithCSVButtonActions}`;

enum HRISSettingsUploadCSVButtonActions {
  Clicked = 'Clicked',
}

type HRISSettingsUploadCSVButtonActionsType =
  `${AnalyticsLocations.HrisSettings} ${HRISSettingsObjects.UploadCSVButton} ${HRISSettingsUploadCSVButtonActions}`;

type HRISSettingsObjectActionsType =
  | HRISSettingsUpdateWithCSVButtonActionsType
  | HRISSettingsUploadCSVButtonActionsType
  | HRISSettingsMergeEmployeesTableRowActionsType
  | HRISSettingsMergeEmployeesTableRowDeleteButtonActionsType;

/**
 * Feature Flags
 * - Narrowing down to just feature flags and actions
 */
enum FeatureFlagsActions {
  Impression = 'Impression',
}

type FeatureFlagsActionType = `${AnalyticsLocations.FeatureFlags} ${FeatureFlagsActions}`;

/**
 * Skills And Culture
 */
enum SkillsAndCultureObjects {
  CaptureTalentModelSnapshotButton = 'Capture Talent Model Snapshot Button',
}

enum SkillsAndCultureCaptureTalentModelSnapshotButtonActions {
  Clicked = 'Clicked',
}

type SkillsAndCultureCaptureTalentModelSnapshotButtonActionsType =
  `${AnalyticsLocations.SkillsAndCulture} ${SkillsAndCultureObjects.CaptureTalentModelSnapshotButton} ${SkillsAndCultureCaptureTalentModelSnapshotButtonActions}`;

type SkillsAndCultureObjectActionType = SkillsAndCultureCaptureTalentModelSnapshotButtonActionsType;

export const SKILLS_AND_CULTURE_ANALYTICS_EVENTS = {
  CAPTURE_TALENT_MODEL_SNAPSHOT_CLICKED: `${AnalyticsLocations.SkillsAndCulture} ${SkillsAndCultureObjects.CaptureTalentModelSnapshotButton} ${SkillsAndCultureCaptureTalentModelSnapshotButtonActions.Clicked}`,
} as Record<string, SkillsAndCultureObjectActionType>;

/**
 * Talent Models
 */
enum TalentModelsObjects {
  ApplyToAJobIntakeButton = 'Apply To A Job Intake Button',
  TakeActionButton = 'Take Action Button',
}

enum TalentModelsApplyToAJobIntakeButtonActions {
  ChooseExistingOne = 'Choose Existing One',
  CreateNew = 'Create New',
}

enum TalentModelsTakeActionButtonActions {
  GenerateSelfAssessment = 'Generate Self Assessment',
}

type TalentModelsCaptureTalentModelSnapshotButtonActionsType =
  | `${AnalyticsLocations.TalentModels} ${TalentModelsObjects.ApplyToAJobIntakeButton} ${TalentModelsApplyToAJobIntakeButtonActions}`
  | `${AnalyticsLocations.TalentModels} ${TalentModelsObjects.TakeActionButton} ${TalentModelsTakeActionButtonActions}`;

type TalentModelsObjectActionType = TalentModelsCaptureTalentModelSnapshotButtonActionsType;

export const TALENT_MODELS_ANALYTICS_EVENTS = {
  APPLY_TO_A_JOB_INTAKE_CREATE_NEW: `${AnalyticsLocations.TalentModels} ${TalentModelsObjects.ApplyToAJobIntakeButton} ${TalentModelsApplyToAJobIntakeButtonActions.CreateNew}`,
  APPLY_TO_A_JOB_INTAKE_CHOOSE_EXISTING_ONE: `${AnalyticsLocations.TalentModels} ${TalentModelsObjects.ApplyToAJobIntakeButton} ${TalentModelsApplyToAJobIntakeButtonActions.ChooseExistingOne}`,
  GENERATE_SELF_ASSESSMET: `${AnalyticsLocations.TalentModels} ${TalentModelsObjects.TakeActionButton} ${TalentModelsTakeActionButtonActions.GenerateSelfAssessment}`,
} as Record<string, TalentModelsObjectActionType>;

// Talent Discovery

enum TalentDiscoveryObjects {
  LeanMoreButton = 'Lean More Button',
  BookADemoButton = 'Book A Demo Button',
  AskAQuestionButton = 'Ask A Question Button',
  FalseDoorImage = 'False Door Image',
}

enum TalentDiscoveryLeanMoreButtonActions {
  Clicked = 'Clicked',
}

enum TalentDiscoveryBookADemoButtonActions {
  Clicked = 'Clicked',
}

enum TalentDiscoveryAskAQuestionButtonActions {
  Clicked = 'Clicked',
}

enum TalentDiscoveryFalseDoorImageButtonActions {
  Clicked = 'Clicked',
}

enum TalentModelLeanMoreButtonLocations {
  HomePage = 'Home Page',
  AllCandidates = 'All Candidates',
  CandidatesWithinAJob = 'Candidates Within A Job',
}

type TalentDiscoveryLeanMoreButtonActionsType =
  | `${AnalyticsLocations.TalentDiscovery} ${TalentDiscoveryObjects.LeanMoreButton} ${TalentDiscoveryLeanMoreButtonActions} ${TalentModelLeanMoreButtonLocations}`;

type TalentDiscoveryBookADemoButtonActionsType =
  | `${AnalyticsLocations.TalentDiscovery} ${TalentDiscoveryObjects.BookADemoButton} ${TalentDiscoveryBookADemoButtonActions}`;

type TalentDiscoveryAskAQuestionButtonActionsType =
  | `${AnalyticsLocations.TalentDiscovery} ${TalentDiscoveryObjects.AskAQuestionButton} ${TalentDiscoveryAskAQuestionButtonActions}`;

type TalentDiscoveryFalseDoorImageButtonActionsType =
  | `${AnalyticsLocations.TalentDiscovery} ${TalentDiscoveryObjects.FalseDoorImage} ${TalentDiscoveryFalseDoorImageButtonActions}`;

type TalentDiscoveryObjectActionType =
  | TalentDiscoveryLeanMoreButtonActionsType
  | TalentDiscoveryBookADemoButtonActionsType
  | TalentDiscoveryAskAQuestionButtonActionsType
  | TalentDiscoveryFalseDoorImageButtonActionsType;

export const TALENT_DISCOVERY_ANALYTICS_EVENTS = {
  LEARN_MORE_BUTTON_CLICKED_HOME_PAGE: `${AnalyticsLocations.TalentDiscovery} ${TalentDiscoveryObjects.LeanMoreButton} ${TalentDiscoveryLeanMoreButtonActions.Clicked} ${TalentModelLeanMoreButtonLocations.HomePage}`,
  LEARN_MORE_BUTTON_CLICKED_ALL_CANDIDATES: `${AnalyticsLocations.TalentDiscovery} ${TalentDiscoveryObjects.LeanMoreButton} ${TalentDiscoveryLeanMoreButtonActions.Clicked} ${TalentModelLeanMoreButtonLocations.AllCandidates}`,
  LEARN_MORE_BUTTON_CLICKED_CANDIDATES_WITHIN_A_JOB: `${AnalyticsLocations.TalentDiscovery} ${TalentDiscoveryObjects.LeanMoreButton} ${TalentDiscoveryLeanMoreButtonActions.Clicked} ${TalentModelLeanMoreButtonLocations.CandidatesWithinAJob}`,
  BOOK_A_DEMO_BUTTON_CLICKED: `${AnalyticsLocations.TalentDiscovery} ${TalentDiscoveryObjects.BookADemoButton} ${TalentDiscoveryBookADemoButtonActions.Clicked}`,
  ASK_A_QUESTION_BUTTON_CLICKED: `${AnalyticsLocations.TalentDiscovery} ${TalentDiscoveryObjects.AskAQuestionButton} ${TalentDiscoveryAskAQuestionButtonActions.Clicked}`,
  FALSE_DOOR_IMAGE_CLICKED: `${AnalyticsLocations.TalentDiscovery} ${TalentDiscoveryObjects.FalseDoorImage} ${TalentDiscoveryFalseDoorImageButtonActions.Clicked}`,
} as Record<string, TalentDiscoveryObjectActionType>;

/**
 * Your Teams
 */
enum YourTeamsObjects {
  GenerateTalentModelButton = 'Generate Talent Model Button',
}

enum YourTeamsGenerateTalentModelButtonActions {
  Generated = 'Generated',
  Cohort1 = 'Cohort 1',
  Cohort2 = 'Cohort 2',
}

type YourTeamsGenerateTalentModelButtonActionsType =
  | `${AnalyticsLocations.YourTeams} ${YourTeamsObjects.GenerateTalentModelButton} ${YourTeamsGenerateTalentModelButtonActions}`;

type YourTeamsObjectActionType = YourTeamsGenerateTalentModelButtonActionsType;

export const YOUR_TEAMS_ANALYTICS_EVENTS = {
  GENERATE_TALENT_MODEL: `${AnalyticsLocations.YourTeams} ${YourTeamsObjects.GenerateTalentModelButton} ${YourTeamsGenerateTalentModelButtonActions.Generated}`,
  GENERATE_TALENT_MODEL_COHORT_1: `${AnalyticsLocations.YourTeams} ${YourTeamsObjects.GenerateTalentModelButton} ${YourTeamsGenerateTalentModelButtonActions.Cohort1}`,
  GENERATE_TALENT_MODEL_COHORT_2: `${AnalyticsLocations.YourTeams} ${YourTeamsObjects.GenerateTalentModelButton} ${YourTeamsGenerateTalentModelButtonActions.Cohort2}`,
} as Record<string, YourTeamsObjectActionType>;

/**
 * Explainability
 */
enum ExplainabilityObjects {
  ExplainerCard = 'Explainer Card',
  ExplainerCardQuestionHeading = 'Explainer Card Question Heading',
}

enum ExplainabilityExplainerCardActions {
  Opened = 'Opened',
}

type ExplainabilityExplainerCardActionsType =
  `${AnalyticsLocations.Explainability} ${ExplainabilityObjects.ExplainerCard} ${ExplainabilityExplainerCardActions}`;

enum ExplainabilityExplainerCardQuestionHeadingActions {
  Clicked = 'Clicked',
}

type ExplainabilityExplainerCardQuestionHeadingActionsType =
  `${AnalyticsLocations.Explainability} ${ExplainabilityObjects.ExplainerCardQuestionHeading} ${ExplainabilityExplainerCardQuestionHeadingActions}`;

type ExplainabilityObjectsActionType =
  | ExplainabilityExplainerCardActionsType
  | ExplainabilityExplainerCardQuestionHeadingActionsType;

export const EXPLAINABILITY_ANALYTICS_EVENTS = {
  EXPLAINER_CARD_OPENED: `${AnalyticsLocations.Explainability} ${ExplainabilityObjects.ExplainerCard} ${ExplainabilityExplainerCardActions.Opened}`,
  EXPLAINER_CARD_QUESTION_CLICKED: `${AnalyticsLocations.Explainability} ${ExplainabilityObjects.ExplainerCardQuestionHeading} ${ExplainabilityExplainerCardQuestionHeadingActions.Clicked}`,
};

/**
 * This type contains all the possible combinations for our analytics events, type-safe
 */
export type AnalyticsEvent =
  | AdminDashboardObjectActionsType
  | ReferenceSurveyObjectActionsType
  | CandidateHubObjectActionsType
  | PDFViewerObjectActionsType
  | QualityOfHireObjectActionsType
  | EmployeeReviewersObjectActionsType
  | CandidateExperienceObjectActionsType
  | IntakeObjectActionsType
  | PositionObjectActionType
  | ActivitiesQueueObjectActionsType
  | HRISSettingsObjectActionsType
  | FeatureFlagsActionType
  | NewSurveyObjectActionsType
  | SurveysHomeObjectActionsType
  | NavBarObjectActionsType
  | SkillsAndCultureObjectActionType
  | TalentModelsObjectActionType
  | ExplainabilityObjectsActionType
  | YourTeamsObjectActionType
  // To do: delete when migrated to new version
  | DeprecatedAnalyticsEvents;

// Below this line is old and due for re-write

export enum AnalyticsUserType {
  REFERENCER = 'referencer',
  CANDIDATE = 'candidate',
  HIRER = 'hirer',
}

enum AnalyticsReferencerPages {
  COMPLETE_RECOMMENDATION = 'complete-recommendation',
  SURVEYS = 'surveys',
}

enum AnalyticsReferencerActions {
  NOTIFY_NOW = 'notify-now',
  NOTIFY_LATER = 'notify-later',
  CLICK_TODO_TAB = 'click-todo-tab',
  CLICK_COMPLETED_TAB = 'click-complete-tab',
  CLICK_SURVEY_BUTTON = 'click-survey-button',
}

type referencerAnalyticsEvent =
  `${AnalyticsUserType.REFERENCER}_${AnalyticsReferencerPages}_${AnalyticsReferencerActions}`;

enum AnalyticsHirerPages {
  CANDIDATE_HUB = 'candidate-hub',
}

enum AnalyticsHirerActions {
  HIGHLIGHT_ANSWER = 'highlight-answer',
  RATE_QUESTION = 'rate-question',
  ONBOARDING_REPORT_VIEW = 'view-onboarding-report',
  FRAMEWORK_VIEW = 'view-framework',
  SELF_ASSESSMENT_VIEW = 'view-self-assessment',
  SELF_ASSESSMENT_COMPARISON_VIEW = 'view-self-assessment-comparison',
}

type hirerAnalyticsEvent =
  `${AnalyticsUserType.HIRER}_${AnalyticsHirerPages}_${AnalyticsHirerActions}`;

enum AnalyticsHirerSettingsPages {
  COMPANY_SETTINGS = 'company-settings',
}

enum AnalyticsHirerSettingsActions {
  HRIS_SETTINGS_VIEW = 'view-hris-settings',
  HRIS_SETTINGS_CLICKED_CONNECT = 'hris-settings-connect',
  HRIS_SETTINGS_SEARCH = 'hris-settings-performed-search',
  HRIS_SWITCHED_TABLE = 'hris-settings-switched-table',
  HRIS_MERGE_EMPLOYEES_TABLE_USED_FILTER = 'hris-settings-merge-employees-used-filter',
  HRIS_MERGE_EMPLOYEES_TABLE_USED_PAGINATION = 'hris-settings-merge-employees-used-pagination',
  HRIS_MERGE_MANAGERS_TABLE_USED_FILTER = 'hris-settings-merge-managers-used-filter',
  HRIS_MERGE_MANAGERS_TABLE_USED_PAGINATION = 'hris-settings-merge-managers-used-pagination',
  HRIS_MERGE_TEAMS_TABLE_USED_PAGINATION = 'hris-settings-merge-teams-used-pagination',
  HRIS_MERGE_LOCATIONS_TABLE_USED_PAGINATION = 'hris-settings-merge-locations-used-pagination',
  HRIS_HIRING_MANAGERS_TABLE_USED_PAGINATION = 'hris-settings-hiring-managers-table-used-pagination',
  HRIS_RECRUITERS_TABLE_USED_PAGINATION = 'hris-settings-recruiters-table-used-pagination',
}

type hirerSettingsAnalyticsEvent =
  `${AnalyticsUserType.HIRER}_${AnalyticsHirerSettingsPages}_${AnalyticsHirerSettingsActions}`;

/**
 * TODO: Remove - for backwards compatibility while I re-write this, we're going to combine the new
 * AnalyticsEvent type with this one
 */
type DeprecatedAnalyticsEvents =
  | referencerAnalyticsEvent
  | hirerAnalyticsEvent
  | hirerSettingsAnalyticsEvent; // Remove AnalyticsEvent once the older events are integrated into the new system

/*
 * Positions
 * These are based on old events, and do not conform to the new paradigm
 */

enum PositionObjects {
  IntakeSurveyRespondent = 'Intake Survey Respondent',
  RecruitingTeam = 'Recruiting Team',
  AddIntakeButton = 'Add Intake Button',
}

enum AddIntakeButtonActions {
  Clicked = 'Clicked',
}

type AddIntakeButtonActionsType =
  `${AnalyticsLocations.PositionHub} ${PositionObjects.AddIntakeButton} ${AddIntakeButtonActions}`;

enum PositionIntakeSurveyRespondentActions {
  RequestedResubmission = 'Requested Resubmission',
  Deleted = 'Deleted',
  Invited = 'Invited',
}

type PositionIntakeSurveyRespondentActionType =
  `${AnalyticsLocations.PositionHub} ${PositionObjects.IntakeSurveyRespondent} ${PositionIntakeSurveyRespondentActions}`;

enum PositionRecruitingTeamActions {
  Invited = 'Invited',
  InvitedByEmail = 'Invited By Email',
}

type PositionRecruitingTeamActionType =
  `${AnalyticsLocations.PositionHub} ${PositionObjects.RecruitingTeam} ${PositionRecruitingTeamActions}`;

export type PositionObjectActionType =
  | PositionIntakeSurveyRespondentActionType
  | PositionRecruitingTeamActionType
  | AddIntakeButtonActionsType;

export const HIRER_POSITION_ANALYTICS_EVENTS = {
  ADD_INTAKE: `${AnalyticsLocations.PositionHub} ${PositionObjects.AddIntakeButton} ${AddIntakeButtonActions.Clicked}`,
  INVITE_NEW_HIRER_BY_EMAIL: `${AnalyticsLocations.PositionHub} ${PositionObjects.RecruitingTeam} ${PositionRecruitingTeamActions.InvitedByEmail}`,
  INVITE_TEAM_MEMBER: `${AnalyticsLocations.PositionHub} ${PositionObjects.RecruitingTeam} ${PositionRecruitingTeamActions.Invited}`,
  INVITE_BENCHMARK_REQUEST: `${AnalyticsLocations.PositionHub} ${PositionObjects.IntakeSurveyRespondent} ${PositionIntakeSurveyRespondentActions.Invited}`,
  DELETE_BENCHMARK_REQUEST: `${AnalyticsLocations.PositionHub} ${PositionObjects.IntakeSurveyRespondent} ${PositionIntakeSurveyRespondentActions.Deleted}`,
  REQUEST_RESUBMISSION_BENCHMARK_REQUEST: `${AnalyticsLocations.PositionHub} ${PositionObjects.IntakeSurveyRespondent} ${PositionIntakeSurveyRespondentActions.RequestedResubmission}`,
} as Record<string, PositionObjectActionType>;
